import React, { useState } from 'react';
import { ChevronLeft, Hash, History, ArrowRight, AlertTriangle, Tag, Calendar, MapPin, X, Phone, Mail, Clock, FileText, MessageSquare, PenTool as Tool, Package, Printer, Share2, MoreVertical, CheckCircle, XCircle, AlertOctagon, User } from 'lucide-react';
import type { Database } from '../../lib/database.types';
import { useVisits, useWorkOrderHistory, useTechnicians } from '../../lib/hooks';
import { usePartsTracking } from '../../hooks/usePartsTracking';
import { supabase } from '../../lib/supabase';
import { PartsTrackingTable } from '../../components/PartsTrackingTable';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from '@/components/ui/use-toast';

type WorkOrder = Database['public']['Tables']['work_orders']['Row'];
type Customer = Database['public']['Tables']['customers']['Row'];

interface WorkOrderDetailsProps {
  order: WorkOrder;
  customer?: Customer;
  onClose: () => void;
}

export const WorkOrderDetails: React.FC<WorkOrderDetailsProps> = ({
  order,
  customer,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const { technicians } = useTechnicians();
  const { visits, loading: visitsLoading, addVisit, updateVisit, deleteVisit } = useVisits(order.id);
  const { parts, loading: partsLoading, addPart, updatePart, deletePart } = usePartsTracking(order.id);
  const { history, loading: historyLoading } = useWorkOrderHistory(order.id);

  const statusColors = {
    'Received': 'bg-gray-100 text-gray-800',
    'Customer Notified': 'bg-blue-100 text-blue-800',
    'Awaiting Customer Response': 'bg-yellow-100 text-yellow-800',
    'Waiting for Parts': 'bg-orange-100 text-orange-800',
    'Technician Ordering Parts': 'bg-purple-100 text-purple-800',
    'Unable to Reach Customer': 'bg-red-100 text-red-800',
    'Canceled': 'bg-red-100 text-red-800',
    'Reassigned': 'bg-indigo-100 text-indigo-800',
    'Visit Scheduled': 'bg-blue-100 text-blue-800',
    'Visit Completed': 'bg-green-100 text-green-800',
    'Needs Second Opinion': 'bg-yellow-100 text-yellow-800',
    'Under Research': 'bg-purple-100 text-purple-800',
    'Billed': 'bg-green-100 text-green-800',
    'Paid': 'bg-emerald-100 text-emerald-800'
  };

  const priorityColors = {
    low: 'bg-gray-100 text-gray-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    emergency: 'bg-red-100 text-red-800',
  };

  const statusOptions = [
    'Received',
    'Customer Notified',
    'Awaiting Customer Response',
    'Waiting for Parts',
    'Technician Ordering Parts',
    'Unable to Reach Customer',
    'Canceled',
    'Reassigned',
    'Visit Scheduled',
    'Visit Completed',
    'Needs Second Opinion',
    'Under Research',
    'Billed',
    'Paid'
  ];

  const handleStatusChange = async (newStatus: string, note?: string) => {
    try {
      setUpdatingStatus(true);
      
      // Update work order status
      const { error } = await supabase
        .from('work_orders')
        .update({ status: newStatus })
        .eq('id', order.id);

      if (error) throw error;

      // Create history record
      const { error: historyError } = await supabase
        .from('work_order_status_history')
        .insert([{
          work_order_id: order.id,
          old_status: order.status,
          new_status: newStatus,
          notes: note || null,
          changed_by: (await supabase.auth.getUser()).data.user?.id
        }]);

      if (historyError) throw historyError;

      // Update local state
      order.status = newStatus;
      setShowStatusConfirmModal(null);
      setShowStatusModal(false);

      toast({
        title: "Status Updated",
        description: `Work order status changed to ${newStatus}`,
      });
    } catch (err) {
      console.error('Failed to update status:', err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : 'Failed to update status',
        variant: "destructive",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePrint = () => {
    // Open print dialog
    window.print();
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: `Work Order ${order.work_order_number}`,
        text: `Work Order Details for ${customer?.first_name} ${customer?.last_name}`,
        url: window.location.href
      });
    } catch (err) {
      // Handle share error or fallback
      console.warn('Share failed:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-fit"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Back to Work Orders
          </Button>

          <div className="flex-1">
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-gray-900">
                Work Order {order.work_order_number}
              </h1>
              <Badge variant="secondary" className={statusColors[order.status]}>
                {order.status}
              </Badge>
              <Badge variant="secondary" className={priorityColors[order.priority]}>
                {order.priority.toUpperCase()}
              </Badge>
            </div>
            <p className="text-gray-500 mt-1">Created {new Date(order.created_at).toLocaleDateString()}</p>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handlePrint}
              title="Print Work Order"
            >
              <Printer className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleShare}
              title="Share Work Order"
            >
              <Share2 className="w-4 h-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowStatusModal(true)}>
                  Update Status
                </DropdownMenuItem>
                <DropdownMenuItem>
                  Create Invoice
                </DropdownMenuItem>
                <DropdownMenuItem className="text-destructive">
                  Delete Work Order
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          <div className="xl:col-span-2 space-y-6">
            {/* Customer Card */}
            {customer && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium">Customer Information</h3>
                  <Button variant="outline" onClick={() => window.dispatchEvent(new CustomEvent('routeChange', { detail: 'customers' }))}>
                    View Profile
                  </Button>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-blue-100 text-blue-600">
                          {customer.first_name[0]}
                          {customer.last_name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="text-lg font-medium">
                          {customer.first_name} {customer.last_name}
                        </h4>
                        <p className="text-sm text-gray-500">Customer since {new Date(customer.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {customer.phone && (
                      <div className="flex items-center text-sm">
                        <Phone className="w-4 h-4 text-gray-400 mr-2" />
                        <a href={`tel:${customer.phone}`} className="text-blue-600 hover:text-blue-800">
                          {customer.phone}
                        </a>
                      </div>
                    )}
                    {customer.email && (
                      <div className="flex items-center text-sm">
                        <Mail className="w-4 h-4 text-gray-400 mr-2" />
                        <a href={`mailto:${customer.email}`} className="text-blue-600 hover:text-blue-800">
                          {customer.email}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center text-sm">
                      <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                      <span>{customer.address}, {customer.city}, {customer.state} {customer.zip_code}</span>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Tabs Navigation */}
            <Card className="p-6">
              <Tabs defaultValue="details" value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-6">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="parts">Parts</TabsTrigger>
                  <TabsTrigger value="visits">Visits</TabsTrigger>
                  <TabsTrigger value="history">History</TabsTrigger>
                </TabsList>

                <TabsContent value="details">
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Service Information</h4>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center space-x-2 text-gray-700">
                            <Tool className="w-4 h-4" />
                            <span className="font-medium">{order.service_type}</span>
                          </div>
                          {order.brand && (
                            <div className="mt-2 text-sm text-gray-600">Brand: {order.brand}</div>
                          )}
                        </div>
                        {order.technician_id && (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center space-x-2 text-gray-700">
                              <User className="w-4 h-4" />
                              <span className="font-medium">
                                {technicians.find(t => t.id === order.technician_id)?.full_name || 'Unassigned'}
                              </span>
                            </div>
                            <div className="mt-2 text-sm text-gray-600">Assigned Technician</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {(order.svo || order.model_number || order.serial_number) && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-2">Equipment Details</h4>
                        <div className="grid sm:grid-cols-3 gap-4">
                          {order.svo && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="text-sm font-medium text-gray-900">{order.svo}</div>
                              <div className="text-sm text-gray-500">SVO Number</div>
                            </div>
                          )}
                          {order.model_number && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="text-sm font-medium text-gray-900">{order.model_number}</div>
                              <div className="text-sm text-gray-500">Model Number</div>
                            </div>
                          )}
                          {order.serial_number && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="text-sm font-medium text-gray-900">{order.serial_number}</div>
                              <div className="text-sm text-gray-500">Serial Number</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Description</h4>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-700 whitespace-pre-wrap">{order.description}</p>
                      </div>
                    </div>

                    {order.notes && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-2">Additional Notes</h4>
                        <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-400">
                          <p className="text-gray-700 whitespace-pre-wrap">{order.notes}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="parts">
                  <PartsTrackingTable
                    parts={parts}
                    onAddPart={addPart}
                    onUpdatePart={updatePart}
                    onDeletePart={deletePart}
                  />
                </TabsContent>

                <TabsContent value="visits">
                  <div className="space-y-6">
                    {visits.map((visit) => (
                      <div key={visit.id} className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="p-2 bg-blue-50 rounded-lg">
                              <Calendar className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="font-medium">{new Date(visit.scheduled_date).toLocaleDateString()}</h4>
                              <p className="text-sm text-gray-500">
                                {visit.time_window_start} - {visit.time_window_end}
                              </p>
                            </div>
                          </div>
                          <Badge variant="secondary" className={
                            visit.status === 'completed' ? 'bg-green-100 text-green-800' :
                            visit.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }>
                            {visit.status}
                          </Badge>
                        </div>
                        {visit.notes && (
                          <div className="mt-4 text-sm text-gray-600">
                            {visit.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="history">
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                    <div className="space-y-8">
                      {history.map((change, index) => (
                        <div key={change.id} className="relative flex items-start group ml-8">
                          <div className="absolute -left-10 mt-1">
                            <div className="w-2.5 h-2.5 rounded-full bg-gray-300 group-hover:bg-blue-500 transition-colors"></div>
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              {change.old_status && (
                                <>
                                  <span className={`px-2.5 py-0.5 rounded-full text-sm font-medium ${statusColors[change.old_status]}`}>
                                    {change.old_status}
                                  </span>
                                  <ArrowRight className="w-4 h-4 text-gray-400" />
                                </>
                              )}
                              <span className={`px-2.5 py-0.5 rounded-full text-sm font-medium ${statusColors[change.new_status]}`}>
                                {change.new_status}
                              </span>
                              <span className="text-sm text-gray-500">
                                {new Date(change.created_at).toLocaleString()}
                              </span>
                            </div>
                            {change.notes && (
                              <p className="mt-1 text-sm text-gray-600">
                                {change.notes}
                              </p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">
                              Changed by {change.changed_by.email}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Button 
                  className="w-full justify-start"
                  onClick={() => setShowStatusModal(true)}
                >
                  <History className="w-4 h-4 mr-2" />
                  Update Status
                </Button>
                <Button 
                  className="w-full justify-start"
                  variant="outline"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
                <Button 
                  className="w-full justify-start"
                  variant="outline"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
                <Button 
                  className="w-full justify-start"
                  variant="outline"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Order Parts
                </Button>
              </div>
            </Card>

            {/* Status Timeline */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Status Timeline</h3>
              <div className="space-y-4">
                {history.slice(0, 5).map((change, index) => (
                  <div key={change.id} className="flex items-start space-x-3">
                    <div className={`p-1.5 rounded-full ${
                      index === 0 ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {index === 0 ? (
                        change.new_status === 'completed' ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : change.new_status === 'cancelled' ? (
                          <XCircle className="w-4 h-4" />
                        ) : (
                          <AlertOctagon className="w-4 h-4" />
                        )
                      ) : (
                        <div className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium text-gray-900">
                        {change.new_status}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(change.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Update Status</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowStatusModal(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {statusOptions.map(status => (
                <button
                  key={status}
                  onClick={() => setShowStatusConfirmModal(status)}
                  disabled={updatingStatus}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    order.status === status
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Status Confirmation Modal */}
      {showStatusConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Confirm Status Change</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowStatusConfirmModal(null)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <p className="text-gray-600 mb-4">
              Are you sure you want to change the status from{' '}
              <Badge variant="secondary" className={statusColors[order.status]}>
                {order.status}
              </Badge>
              {' '}to{' '}
              <Badge variant="secondary" className={statusColors[showStatusConfirmModal]}>
                {showStatusConfirmModal}
              </Badge>
              ?
            </p>

            <div className="mb-4">
              <Label>Add a note (optional)</Label>
              <Textarea
                id="status-note"
                rows={3}
                className="mt-1"
                placeholder="Enter any additional notes about this status change..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowStatusConfirmModal(null)}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const note = (document.getElementById('status-note') as HTMLTextAreaElement).value;
                  await handleStatusChange(showStatusConfirmModal, note);
                }}
                disabled={updatingStatus}
              >
                {updatingStatus ? 'Updating...' : 'Confirm Change'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};